<template>
  <c-box>
    <c-tabs
      variant="soft-rounded"
      :is-manual="true"
      @change="(index) => (activeBulletinTab = index)"
    >
      <mobile-tab-menu
        v-chakra
        :d="{ lg: 'none' }"
        :mt="{ base: '3xl', sm: '4xl' }"
        :mb="{ base: 'md', sm: 'lg' }"
        :currentPage="String($t(bulletinTabTranslationKeys[activeBulletinTab]))"
      >
        <template>
          <c-tab-list
            display="block"
            mx="-md"
            v-chakra="{
              '& button[aria-selected=true], & button[data-selected]': {
                color: 'white',
                bg: 'brand.purple',
                borderColor: 'brand.purple',
              },
            }"
          >
            <c-tab px="xl" py="sm" font-weight="normal">
              {{ $t("bulletin.shortlist.title") }}
            </c-tab>
            <c-tab px="xl" py="sm" font-weight="normal">
              {{ $t("bulletin.category.technicalUpdates") }}
            </c-tab>
          </c-tab-list>
        </template>
      </mobile-tab-menu>

      <c-flex :mb="{ base: 'md', lg: '4xl' }" align-items="center">
        <c-tab-list
          :d="{ base: 'none', lg: 'flex' }"
          v-chakra="{
            '& button[aria-selected=true], & button[data-selected]': {
              color: 'white',
              bg: 'brand.purple',
              borderColor: 'brand.purple',
            },
          }"
        >
          <c-tab px="3xl" py="sm" font-weight="normal">{{ $t("bulletin.shortlist.title") }}</c-tab>
          <c-tab px="3xl" py="sm" font-weight="normal">
            {{ $t("bulletin.category.technicalUpdates") }}
          </c-tab>
        </c-tab-list>

        <c-box ml="auto">
          <router-link to="/bulletin">
            <c-button right-icon="chevron_right" tabindex="-1">
              {{ $t("bulletin.linkToListBulletin") }}
            </c-button>
          </router-link>
        </c-box>
      </c-flex>

      <c-tab-panels>
        <!-- bulletins -->
        <c-tab-panel>
          <c-grid :gap="{ base: 'sm', lg: 'xl' }" :template-columns="{ lg: 'repeat(4, 1fr)' }">
            <!-- col-span doesn't support object syntax, use array syntax instead -->
            <c-grid-item
              v-for="(item, index) in bulletinList"
              :key="item.link"
              :col-span="index === 0 ? [1, 1, 2] : '1'"
            >
              <router-link :to="{ name: 'bulletin', params: { id: item.id } }">
                <c-box
                  background="#4b4b4b"
                  :background-image="item.featured_image_url && `url(${item.featured_image_url})`"
                  background-size="cover"
                  background-position="center"
                  background-repeat="no-repeat"
                  border-radius="sharper"
                  min-height="330px"
                  overflow="hidden"
                  position="relative"
                  color="white"
                  v-chakra="{
                    '&::before': {
                      content: '\'\'',
                      position: 'absolute',
                      top: '0',
                      left: '0',
                      width: '100%',
                      height: '100%',
                      background: 'rgba(0, 0, 0, 0.4)',
                    },
                  }"
                >
                  <c-box
                    v-if="item.bulletin_category.length > 0"
                    z-index="1"
                    position="absolute"
                    top="3rem"
                    left="0"
                    px="xl"
                    py="xs"
                    border-top-right-radius="full"
                    border-bottom-right-radius="full"
                    background="brand.beige"
                    color="black"
                    font-size="xs"
                  >
                    <c-text>
                      {{ item.bulletin_category[0] }}
                    </c-text>
                  </c-box>
                  <c-box z-index="1" position="absolute" bottom="3rem" left="xl" right="xl">
                    <c-heading as="h4" font-size="xl" font-weight="normal">
                      <span bulletin v-html="item.title" />
                    </c-heading>
                  </c-box>
                </c-box>
              </router-link>
            </c-grid-item>
          </c-grid>
        </c-tab-panel>

        <!-- technical updates -->
        <c-tab-panel>
          <c-box :mx="{ md: 'xl' }">
            <c-box
              v-if="technicalList.length === 0"
              border-bottom="1px solid"
              border-bottom-color="brand.grayDark"
            >
              <c-box>
                <p>{{ $t("bulletin.technicalUpdates.notFound") }}</p>
              </c-box>
            </c-box>
            <template v-else>
              <c-box
                v-for="(item, index) in technicalList"
                :key="item.link"
                border-bottom="1px solid"
                border-bottom-color="brand.grayDark"
              >
                <router-link :to="{ name: 'bulletin', params: { id: item.id } }">
                  <c-flex
                    :flex-direction="{ base: 'column', md: 'row' }"
                    :mt="index === 0 ? { base: '-xl', md: '-3rem' } : '0'"
                    :py="{ base: '3xl', md: '3rem' }"
                  >
                    <c-box
                      :min-width="{ md: '10rem' }"
                      font-size="xl"
                      line-height="1.2"
                      :mb="{ base: 'xs', md: '0' }"
                    >
                      <c-text>{{ item.date | moment("D.M.YYYY") }}</c-text>
                    </c-box>
                    <c-box>
                      <c-heading
                        as="h4"
                        font-size="xl"
                        font-weight="normal"
                        bulletin
                        v-html="item.title"
                      />
                      <c-box mt="xl" v-html="item.content" />
                    </c-box>
                  </c-flex>
                </router-link>
              </c-box>
            </template>
          </c-box>
        </c-tab-panel>
      </c-tab-panels>
    </c-tabs>
  </c-box>
</template>

<script lang="ts">
import MobileTabMenu from "@/components/MobileTabMenu.vue";
import {
  CBox,
  CButton,
  CFlex,
  CGrid,
  CGridItem,
  CHeading,
  CTab,
  CTabList,
  CTabPanel,
  CTabPanels,
  CTabs,
  CText,
} from "@chakra-ui/vue";
import { defineComponent } from "vue";

export default defineComponent({
  name: "ShortListBulletin",
  components: {
    CTabs,
    CTabList,
    CTabPanels,
    CTab,
    CTabPanel,
    CBox,
    CButton,
    CHeading,
    CText,
    CGrid,
    CGridItem,
    MobileTabMenu,
    CFlex,
  },
  data: function () {
    return {
      activeBulletinTab: 0,
      bulletinTabTranslationKeys: [
        "bulletin.shortlist.title",
        "bulletin.category.technicalUpdates",
      ],
    };
  },
  computed: {
    bulletinList(): any[] {
      return this.$store.state.bulletin.bulletin.slice(0, 3);
    },
    technicalList(): any[] {
      return this.$store.state.bulletin.technical.slice(0, 4);
    },
  },
});
</script>
